import React, { useContext, useState, useEffect, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { orderBy, filterBy } from '@progress/kendo-data-query';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import Loader from '../../components/Loader';
import RepositoryItemEditor from '../../components/RepositoryItemEditor';
import { useRepositoryHook } from '../../hooks';
import { LocalizationContext } from '../../contexts';
import { stringScoreNormalizer } from '../../utils/helper';
import './index.scss';

export default function RepositoryManagement({ match }) {
    const history = useHistory();
    const [localization] = useContext(LocalizationContext);
    const { disciplineID, itemID } = match.params;

    const {
        isLoadingDimensions,
        isErrorDimensions,
        dimensions,
        disciplines,
        itemTypes,
        itemSources,
        itemContexts,
        ageGrades,
        loadItemsByDiscipline,
        isItemsLoading,
        errorItemsLoading,
        items,
        clearErrorItemsLoading,
        isEditItemLoading,
        errorEditItemLoading,
        clearErrorEditItemLoading,
        editItem,
        editItemDimensions,
        getEditItem,
        updateItem,
        deleteItem,
        isItemDeleting,
        errorItemDeleting,
        clearErrorItemDeleting,
        createItem
    } = useRepositoryHook();
    const [errorDisciplinesLoading, setErrorDisciplinesLoading] = useState(null);

    const [gridPaging, setGridPaging] = useState({ skip: 0, take: 20 });
    const [gridSort, setGridSort] = useState({ allowUnsort: true, multiple: false, sort: [{ field: "lastUpdatedDate", dir: "desc" }] });
    const [gridFilter, setGridFilter] = useState({ logic: "and", filters: [] });
    const [gridItems, setGridItems] = useState([]);
    const [searchLanguageID, setSearchLanguageID] = useState('all');

    useEffect(() => {

        return () => { };
    }, []);

    useEffect(() => {
        setErrorDisciplinesLoading(isErrorDimensions);
    }, [isErrorDimensions]);

    useLayoutEffect(() => {
        loadItemsByDiscipline(disciplineID, searchLanguageID);
    }, [disciplineID, searchLanguageID]);

    useLayoutEffect(() => {
        updateGridItems(items, gridSort.sort, gridFilter);
    }, [items]);

    useLayoutEffect(() => {
        getEditItem(itemID);
    }, [itemID]);

    const updateGridItems = (items, sort, filter) => {
        var sortItems = orderBy(items, sort);
        var filters = filter;
        if (filters && filters.filters && filters.filters.length > 0) {
            filters = { ...filter };
            filters.filters.filter(f => f.field === 'title').map(f => {
                f.value = f.value ? stringScoreNormalizer(f.value) : '';
            })
        }
        var filterItems = filterBy(sortItems, filters);
        setGridItems(filterItems);
    };

    const sortChange = (event) => {
        setGridSort({ ...gridSort, sort: event.sort });
        updateGridItems([...items], event.sort, gridFilter);
    }

    const disciplinesList = disciplines ? [(() => {
        let all_disciplines = { id: "all" };
        all_disciplines[localization.labels.repositoryManagement.disciplines_textField] = localization.labels.repositoryManagement.disciplines_allDisciplines;
        return all_disciplines;
    })(), ...disciplines] : false;

    var current_discipline = disciplinesList ? disciplinesList.filter(d => d.id === disciplineID) : false;
    current_discipline = current_discipline && current_discipline.length > 0 ? current_discipline[0] : false;

    // This is a list of the items *content* languages in the repository, it is not related to the localization of the system
    const languages = [
        { id: "he", nameHe: localization.labels.he },
        { id: "ar", nameHe: localization.labels.ar },
        { id: "en", nameHe: localization.labels.en }
    ];

    const languagesList = [(() => {
        let all_languages = { id: "all" };
        all_languages[localization.labels.repositoryManagement.languages_textField] = localization.labels.repositoryManagement.languages_allLanguages;
        return all_languages;
    })(), ...languages];

    var current_language = languagesList ? languagesList.filter(d => d.id === searchLanguageID) : false;
    current_language = current_language?.length > 0 ? current_language[0] : false;


    var titleColumnProps = { width: '180px' };
    const columns = [
        { key: 'id', type: 'text', props: { width: '180px' } },
        { key: 'itemTypes', type: 'text', select: itemTypes.map(itemType => { return itemType[localization.labels.repositoryManagement.itemType_textField]; }), props: { width: '180px' } },
        { key: 'ageGrades', type: 'text', select: ageGrades.map(itemType => { return itemType[localization.labels.repositoryManagement.ageGrade_textField]; }), props: { width: '180px' } },
        {
            key: 'title', type: 'text', props: {
                ...titleColumnProps, cell: (props) => (
                    <td role="gridcell" className="app-repository_management-grid-column--title" onClick={() => { history.push('/repository/' + disciplineID + '/' + props.dataItem.id); }}>
                        {props.dataItem[props.field].split('<!--###-->')[0]}
                    </td>
                )
            }
        },
        { key: 'itemSource', type: 'text', select: itemSources.map(itemSource => { return itemSource[localization.labels.repositoryManagement.itemSource_textField]; }), props: { width: '215px' } },
        { key: 'itemContext', type: 'text', select: itemContexts.map(itemContext => { return itemContext[localization.labels.repositoryManagement.itemContext_textField]; }), props: { width: '180px' } },
        { key: 'addedDate', type: 'date', props: { format: "{0:dd/MM/y HH:mm}", className: "gridcell-ltr", width: '180px' } },
        { key: 'lastUpdatedDate', type: 'date', props: { format: "{0:dd/MM/y HH:mm}", className: "gridcell-ltr", width: '180px' } },
        { key: 'isPublished', type: 'boolean', props: { width: '120px' } },
        { key: 'lastCatalogPublishedDate', type: 'date', props: { format: "{0:dd/MM/y HH:mm}", className: "gridcell-ltr", width: '180px' } },
        { key: 'informaticApprovalDate', type: 'date', props: { format: "{0:dd/MM/y HH:mm}", className: "gridcell-ltr", width: '180px' } },
        { key: 'designApproved', type: 'boolean', props: { width: '120px' } },
    ].map((column, i) => {
        var props = column.props || {};
        props.field = column.key;
        props.filter = column.type;
        props.title = column.key in localization.labels.repositoryManagement.searchResult ? localization.labels.repositoryManagement.searchResult[column.key] : column.key;
        var cell = false;
        var columnMenu = false;
        var filterCell = false;
        switch (column.type) {
            case 'text':
                if (column.select) {
                    filterCell = (props) => {
                        return <div className="k-filtercell">
                            <DropDownList
                                data={column.select}
                                value={props.value || ''}
                                onChange={event => {
                                    const hasValue = (event.target.value && event.target.value != '');
                                    props.onChange({
                                        value: hasValue ? event.target.value : '',
                                        operator: hasValue ? 'contains' : '',//'eq'
                                        syntheticEvent: event.syntheticEvent
                                    });
                                }}
                            />
                            {(props.value && props.value !== '') && <button
                                className="k-button k-button-icon k-clear-button-visible"
                                onClick={event => {
                                    event.preventDefault();
                                    props.onChange({
                                        value: '',
                                        operator: '',
                                        syntheticEvent: event
                                    });
                                }}
                            ><span className="k-icon k-i-filter-clear" /></button>}
                        </div>
                    }
                }
                break;
            case 'numeric':
                break;
            case 'boolean':
                cell = props => (
                    <td role="gridcell">
                        <Checkbox disabled={false} style={{ pointerEvents: 'none' }} value={props.dataItem[props.field]} />
                    </td>
                );
                break;
            case 'date':
                break;
            default:
                break;
        }
        if (cell) props.cell = cell;
        if (columnMenu) props.columnMenu = columnMenu;
        if (filterCell) props.filterCell = filterCell;
        return <Column {...props} dir="ltr" key={'Column_' + i} />;
    });

    var rowNum = 0;
    const getRowNumber = () => {
        rowNum++;
        return rowNum;
    };

    const _export = React.useRef(null);
    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save();
        }
    };

    return (<div className="app-repository_management">
        {(isLoadingDimensions || isItemsLoading || isEditItemLoading || isItemDeleting) && <Loader />}
        {(errorDisciplinesLoading || errorItemsLoading || errorEditItemLoading) && <Dialog onClose={() => { setErrorDisciplinesLoading(false); clearErrorItemsLoading(false); clearErrorEditItemLoading(false); }} title={localization.labels.loading_error_title}><p>{localization.labels.loading_error_message}</p></Dialog>}
        {(errorItemDeleting) && <Dialog onClose={() => { clearErrorItemDeleting(); }} title={localization.labels.delete_error_title}><p>{localization.labels.delete_error_message}</p></Dialog>}
        <div className="app-repository_management-grid">
            <ExcelExport data={gridItems} ref={_export}>
                <Grid
                    className="app-grid"
                    data={gridItems.slice(gridPaging.skip, gridPaging.take + gridPaging.skip)}
                    skip={gridPaging.skip}
                    take={gridPaging.take}
                    total={gridItems.length}
                    pageable={{
                        buttonCount: 3,
                        info: true,
                        type: 'numeric',
                        pageSizes: [5, 10, 15, 20, 25, 30, 40, 50, 100],
                        previousNext: true,
                        messages: localization.labels.grid_messages
                    }}
                    onPageChange={(event) => { setGridPaging({ skip: event.page.skip, take: event.page.take }); }}

                    sortable={{
                        allowUnsort: gridSort.allowUnsort,
                        mode: gridSort.multiple ? 'multiple' : 'single'
                    }}
                    sort={gridSort.sort}
                    onSortChange={sortChange}

                    filterable
                    filter={gridFilter}
                    filterOperators={{
                        date: [
                            { text: 'grid.filterAfterOperator', operator: 'gt' },
                            { text: 'grid.filterBeforeOperator', operator: 'lt' },
                        ],
                        'text': [
                            { text: 'grid.filterContainsOperator', operator: 'contains' },
                            { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
                            { text: 'grid.filterEqOperator', operator: 'eq' },
                            { text: 'grid.filterNotEqOperator', operator: 'neq' },
                            { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
                            { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
                            { text: 'grid.filterIsNullOperator', operator: 'isnull' },
                            { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
                            { text: 'grid.filterIsEmptyOperator', operator: 'isempty' },
                            { text: 'grid.filterIsNotEmptyOperator', operator: 'isnotempty' }
                        ],
                    }}
                    onFilterChange={(e) => {
                        setGridPaging({ ...gridPaging, skip: 0 });
                        setGridFilter(e.filter);
                        updateGridItems([...items], gridSort.sort, e.filter);
                    }}
                >
                    <GridToolbar>
                        <div className="app-repository_management-grid-tools">
                            <div className="app-repository_management-grid-tools--left">
                                {
                                    disciplines &&
                                    <DropDownList
                                        data={disciplinesList}
                                        value={current_discipline}
                                        textField={localization.labels.repositoryManagement.disciplines_textField}
                                        dataItemKey="id"
                                        label={localization.labels.repositoryManagement.disciplines_label}
                                        onChange={(event) => { history.push('/repository/' + event.target.value.id); }}
                                    />
                                }
                                {
                                    languages &&
                                    <DropDownList
                                        data={languagesList}
                                        value={current_language}
                                        textField={localization.labels.repositoryManagement.languages_textField}
                                        dataItemKey="id"
                                        label={localization.labels.repositoryManagement.languages_label}
                                        onChange={(event) => {
                                            setSearchLanguageID(event.target.value.id);
                                        }}
                                    />
                                }
                            </div>
                            <div className="app-repository_management-grid-tools--right">
                                {
                                    false && current_discipline &&
                                    <Button className="new_item k-button" primary={true} onClick={() => {
                                        createItem();
                                    }}>{localization.labels.repositoryManagement.new_item}</Button>
                                }
                            </div>

                            <button
                                title="Export Excel"
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                onClick={excelExport}
                            >
                                Export to Excel
                            </button>

                        </div>
                    </GridToolbar>
                    <Column
                        sortable={false}
                        filterable={false}
                        width="48px"
                        locked={true}
                        cell={props => (
                            <td role="gridcell" style={props.style} className="grid-row_num k-grid-content-sticky">
                                {getRowNumber() + gridPaging.skip}
                            </td>
                        )}
                    />
                    {columns}
                </Grid>
            </ExcelExport>
        </div>
        {current_discipline && editItem && editItemDimensions && <RepositoryItemEditor editItem={editItem} editItemDimensions={editItemDimensions}
            dimensions={dimensions.dimensions} dependencies={dimensions.dependencies}
            backLink={'/repository/' + disciplineID} update={updateItem} deleteItem={deleteItem}></RepositoryItemEditor>}
    </div>);
};